import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate, getStatusInfo, getResponsiveThumbnailUrl } from '@/lib/utils';
import { ShoppingBag, Package } from 'lucide-react';

const DashboardOrdersTab = ({ orders, type }) => {
    const navigate = useNavigate();
    const isBuying = type === 'buying';

    if (orders.length === 0) {
        return (
            <Card className="rounded-2xl">
                <CardContent className="py-12 text-center">
                    {isBuying ? (
                        <>
                            <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-semibold mb-2">Nu ai cumpărat nimic încă</h3>
                            <p className="text-muted-foreground mb-4">Explorează listările disponibile</p>
                            <Button className="rounded-full" onClick={() => navigate('/')}>
                                Explorează
                            </Button>
                        </>
                    ) : (
                        <>
                            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-semibold mb-2">Nu ai vânzări încă</h3>
                            <p className="text-muted-foreground">Vânzările tale vor apărea aici</p>
                        </>
                    )}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {orders.map(order => {
                const statusInfo = getStatusInfo(order.order_status);
                const imageUrl = order.listing_image?.startsWith('/api') 
                    ? `${process.env.REACT_APP_BACKEND_URL}${order.listing_image}` 
                    : order.listing_image;
                // Use thumbnail for bandwidth savings
                const thumbnailUrl = imageUrl ? getResponsiveThumbnailUrl(imageUrl, 200, 150, 75) : null;
                return (
                    <Card 
                        key={order.order_id} 
                        className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/orders/${order.order_id}`)}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    {thumbnailUrl ? (
                                        <img
                                            src={thumbnailUrl}
                                            alt={order.listing_title}
                                            className="w-16 h-16 rounded-xl object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center">
                                            <Package className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold">{order.listing_title}</p>
                                        <p className="text-sm text-muted-foreground">
                                            Comandă #{order.order_id.slice(-8)}
                                            {isBuying && (order.seller_name || order.seller_username) && (
                                                <span> • Vânzător: <span className="font-medium">{order.seller_name || order.seller_username}</span></span>
                                            )}
                                            {!isBuying && (order.buyer_name || order.buyer_username) && (
                                                <span> • Cumpărător: <span className="font-medium">{order.buyer_name || order.buyer_username}</span></span>
                                            )}
                                        </p>
                                        <Badge className={`mt-1 bg-${statusInfo.color}-50 text-${statusInfo.color}-700`}>
                                            {statusInfo.label}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {isBuying ? (
                                        <p className="font-semibold">{formatCurrency(order.amount)}</p>
                                    ) : (
                                        <>
                                            <p className="font-semibold text-green-600">
                                                +{formatCurrency(order.seller_amount)}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                (din {formatCurrency(order.amount)})
                                            </p>
                                        </>
                                    )}
                                    <p className="text-sm text-muted-foreground">
                                        {formatDate(order.created_at)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};

export default DashboardOrdersTab;
